import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { EmptyState, Mono, Table, Td, Th } from './shared';
import MemberDisplay from './MemberDisplay';
import WorkspaceDisplay from './WorkspaceDisplay';
import TraceTimeline from './TraceTimeline';
import { assembleTrace } from './traceUtils';
import { computeSpanCost, formatCost } from './formatUtils';

const HeaderTable = styled(Table)`
  margin-bottom: 0.75rem;
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 30rem;
  font-weight: ${weights.medium};
`;

const PreviewText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeletedBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.danger};
  background: ${colors.danger}15;
  padding: 0.125rem 0.375rem;
  border-radius: 0.125rem;
`;

const SummarySection = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div`
  flex: 1;
  background: ${colors.grey5};
  border: solid 1px ${colors.grey10};
  border-radius: 0.375rem;
  padding: 1rem 1.25rem;
`;

const SummaryCardLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.grey40};
  text-transform: uppercase;
  margin-bottom: 0.5rem;
`;

const SummaryCardTotal = styled.div`
  font-size: 1.5rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  line-height: 1.2;
  margin-bottom: 0.5rem;
`;

const ReplayContainer = styled.div`
  background: ${colors.white};
  border: solid 1px ${colors.grey20};
  border-radius: 0.375rem;
  padding: 1rem;
`;

const SummaryCardDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  font-size: 0.6875rem;
  color: ${colors.grey40};

  strong {
    font-weight: ${weights.bold};
    color: ${colors.grey55};
  }
`;

function formatTokens(n) {
  if (n == null) return '-';
  return n.toLocaleString();
}

export default function ConversationDetail() {
  const { conversationId } = useParams();
  const api = useApi();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTrace = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data: result } = await api.admin.aiReplay.getTrace(conversationId);
        if (!cancelled) setData(result);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTrace();
    return () => {
      cancelled = true;
    };
  }, [api, conversationId]);

  const turns = useMemo(() => {
    if (!data?.events) return [];
    return assembleTrace(data.events);
  }, [data]);

  const summary = useMemo(() => {
    if (!turns.length || !data?.pricing) return null;
    let inputTokens = 0;
    let cachedTokens = 0;
    let outputTokens = 0;
    let inputCost = 0;
    let cachedInputCost = 0;
    let outputCost = 0;
    let cacheSavings = 0;
    let hasAny = false;

    for (const turn of turns) {
      for (const span of turn.spans) {
        const usage = span.response?.eventData?.usage;
        if (!usage) continue;
        const model = span.request?.eventData?.model || span.response?.eventData?.model;
        const cost = computeSpanCost(usage, data.pricing[model]);

        inputTokens += usage.input_tokens ?? 0;
        cachedTokens += usage.input_tokens_details?.cached_tokens ?? 0;
        outputTokens += usage.output_tokens ?? 0;
        hasAny = true;

        if (cost) {
          inputCost += cost.inputCost;
          cachedInputCost += cost.cachedInputCost;
          outputCost += cost.outputCost;
          cacheSavings += cost.cacheSavings;
        }
      }
    }

    if (!hasAny) return null;

    const totalTokens = inputTokens + outputTokens;
    const cacheHitRate = inputTokens > 0 ? ((cachedTokens / inputTokens) * 100).toFixed(1) : null;
    const totalCost = inputCost + cachedInputCost + outputCost;

    return {
      inputTokens,
      cachedTokens,
      outputTokens,
      totalTokens,
      cacheHitRate,
      cacheSavings,
      inputCost,
      cachedInputCost,
      outputCost,
      totalCost,
    };
  }, [turns, data?.pricing]);

  const userTurns = turns.filter((t) => t.userMessage);
  const userTurnCount = userTurns.length;
  const lastMessageEvent = [...(data?.events || [])]
    .reverse()
    .find((e) => e.eventType === 'message' && e.role === 'user');

  return (
    <>
      {isLoading && <EmptyState>Loading conversation trace...</EmptyState>}

      {!isLoading && !data && <EmptyState>Conversation not found.</EmptyState>}

      {!isLoading && data && (
        <>
          <HeaderTable>
            <thead>
              <tr>
                <Th>Conversation</Th>
                <Th>Workspace</Th>
                <Th>Member</Th>
                <Th>Turns</Th>
                <Th>Last Activity</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>
                  <Preview>
                    <PreviewText>{data.title || '-'}</PreviewText>
                    {data.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
                  </Preview>
                  <Mono>{conversationId}</Mono>
                </Td>
                <Td>
                  <WorkspaceDisplay workspace={data.workspace} />
                </Td>
                <Td>
                  <MemberDisplay member={data.member} />
                </Td>
                <Td>{userTurnCount}</Td>
                <Td title={lastMessageEvent?.timestamp}>
                  {lastMessageEvent?.timestamp ? moment(lastMessageEvent.timestamp).fromNow() : '-'}
                </Td>
              </tr>
            </tbody>
          </HeaderTable>

          {summary && (
            <SummarySection>
              <SummaryCard>
                <SummaryCardLabel>Total Tokens</SummaryCardLabel>
                <SummaryCardTotal>{formatTokens(summary.totalTokens)}</SummaryCardTotal>
                <SummaryCardDetails>
                  <span>
                    <strong>{formatTokens(summary.inputTokens)}</strong> input
                  </span>
                  <span>
                    <strong>{formatTokens(summary.cachedTokens)}</strong> cached
                  </span>
                  <span>
                    <strong>{formatTokens(summary.outputTokens)}</strong> output
                  </span>
                </SummaryCardDetails>
              </SummaryCard>
              <SummaryCard>
                <SummaryCardLabel>Total Cost</SummaryCardLabel>
                <SummaryCardTotal>{formatCost(summary.totalCost)}</SummaryCardTotal>
                <SummaryCardDetails>
                  <span>
                    <strong>{formatCost(summary.inputCost)}</strong> input
                  </span>
                  <span>
                    <strong>{formatCost(summary.cachedInputCost)}</strong> cached
                  </span>
                  <span>
                    <strong>{formatCost(summary.outputCost)}</strong> output
                  </span>
                </SummaryCardDetails>
              </SummaryCard>
              {summary.cacheHitRate != null && (
                <SummaryCard>
                  <SummaryCardLabel>Cache Hit Rate</SummaryCardLabel>
                  <SummaryCardTotal>{summary.cacheHitRate}%</SummaryCardTotal>
                  <SummaryCardDetails>
                    <span>
                      <strong>{formatCost(summary.cacheSavings)}</strong> saved
                    </span>
                    <span>
                      <strong>{formatCost(summary.totalCost + summary.cacheSavings)}</strong> without cache
                    </span>
                  </SummaryCardDetails>
                </SummaryCard>
              )}
            </SummarySection>
          )}

          <ReplayContainer>
            <TraceTimeline turns={turns} pricing={data.pricing} />
          </ReplayContainer>
        </>
      )}
    </>
  );
}
